import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ArrowPathIcon, PlusIcon } from "@heroicons/react/24/outline";
import Button from "../../components/ui/Button";
import { IMcpTool } from "../../interfaces/endpoints/IMcpTool";
import {
  getAllMcpTools,
  updateMcpTool
} from "../../services/Mcp.service";
import McpToolModal from "./McpToolModal";
import McpToolsTable from "./McpToolsTable";
import McpToolFilters from "./McpToolFilters";

const McpSettings: React.FC = () => {
  const [tools, setTools] = useState<IMcpTool[]>([]);
  const [filtered, setFiltered] = useState<IMcpTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [editTool, setEditTool] = useState<IMcpTool | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  const fetchTools = () => {
    setLoading(true);
    getAllMcpTools()
      .then((res) => {
        const d = Array.isArray(res.data) ? res.data : [];
        setTools(d);
        setFiltered(d);
      })
      .catch(() => toast.error("Failed to load MCP tools"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTools();
  }, []);

  useEffect(() => {
    let r = tools;
    if (search) {
      const kw = search.toLowerCase();
      r = r.filter(
        (t) => t.toolName.toLowerCase().includes(kw) ||
          t.endpoint.toLowerCase().includes(kw)
      );
    }
    if (category) {
      r = r.filter((t) => t.category === category);
    }
    setFiltered(r);
  }, [search, category, tools]);

  const categories = Array.from(
    new Set(tools.map((t) => t.category).filter(Boolean))
  );

  const handleToggle = async (tool: IMcpTool) => {
    try {
      await updateMcpTool(tool.id, { ...tool, isEnabled: !tool.isEnabled });
      setTools((p) =>
        p.map((t) => (t.id === tool.id ? { ...t, isEnabled: !t.isEnabled } : t))
      );
    } catch {
      toast.error("Failed to update tool");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold">MCP Tools</h1>
        <div className="flex gap-2">
          <Button className="flex items-center gap-1" onClick={() => setShowCreate(true)}>
            <PlusIcon className="w-4 h-4" /> Add New Tool
          </Button>
          <Button className="flex items-center gap-1" onClick={fetchTools}>
            <ArrowPathIcon className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>
      <McpToolFilters
        search={search} onSearch={setSearch}
        category={category} onCategory={setCategory}
        categories={categories}
      />
      {loading ? <p className="text-gray-500 p-4">Loading...</p> : (
        <McpToolsTable tools={filtered} onToggle={handleToggle} onEdit={setEditTool} />
      )}
      <p className="mt-2 text-sm text-gray-500">{filtered.length} of {tools.length} tools</p>
      {editTool && (
        <McpToolModal
          mode="edit"
          tool={editTool}
          onClose={() => setEditTool(null)}
          onSaved={() => {
            setEditTool(null);
            fetchTools();
          }}
        />
      )}
      {showCreate && (
        <McpToolModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            fetchTools();
          }}
        />
      )}
    </div>
  );
};

export default McpSettings;
